import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/utils-format";
import { Coins, ArrowDown, DownloadSimple } from "@phosphor-icons/react";
import { downloadCsv } from "@/lib/exports";

export default function Receivers() {
  const [accounts, setAccounts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ amount: "", method: "Bank Transfer", reference: "", notes: "" });

  const load = () => api.get("/payment-accounts", { params: { country: "India" } }).then((r) => setAccounts(r.data));
  useEffect(() => { load(); }, []);

  const openAccount = async (acc) => {
    setSelected(acc);
    const r = await api.get(`/payment-accounts/${acc.account_id}/summary`);
    setSummary(r.data);
  };

  const addCollection = async (e) => {
    e.preventDefault();
    try {
      await api.post("/collections", {
        payment_account_id: selected.account_id,
        amount: parseFloat(form.amount) || 0,
        method: form.method, reference: form.reference, notes: form.notes,
      });
      toast.success("Collection recorded");
      setOpen(false);
      setForm({ amount: "", method: "Bank Transfer", reference: "", notes: "" });
      openAccount(selected);
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="p-8" data-testid="receivers-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-1">Collections</p>
          <h1 className="text-4xl font-black tracking-tighter">Receivers</h1>
          <p className="text-sm text-zinc-500 mt-2">Money received in India accounts and their collection status.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => downloadCsv("/exports/commissions", "commissions.csv")}
            className="border-zinc-300 rounded-md" data-testid="export-commissions-button">
            <DownloadSimple size={14} weight="bold" className="mr-1" /> Commissions CSV
          </Button>
          <Button variant="outline" onClick={() => downloadCsv("/exports/collections", "collections.csv")}
            className="border-zinc-300 rounded-md" data-testid="export-collections-button">
            <DownloadSimple size={14} weight="bold" className="mr-1" /> Collections CSV
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Accounts List */}
        <div className="lg:col-span-1 border border-zinc-200 bg-white max-h-[70vh] overflow-y-auto">
          <div className="p-4 border-b border-zinc-200"><h3 className="text-sm font-bold uppercase tracking-widest">Indian Accounts</h3></div>
          {accounts.map((a) => (
            <button key={a.account_id} onClick={() => openAccount(a)}
              className={`w-full text-left p-4 border-b border-zinc-100 hover:bg-zinc-50 transition-colors ${selected?.account_id === a.account_id ? "bg-zinc-100" : ""}`}
              data-testid={`receiver-${a.account_id}`}>
              <p className="text-sm font-medium">{a.owner || a.account_name}</p>
              <p className="text-xs text-zinc-500 font-mono">₹{a.commission_per_transaction}/txn</p>
            </button>
          ))}
          {accounts.length === 0 && <p className="p-4 text-sm text-zinc-500">No Indian accounts. Add one from Payment Accounts.</p>}
        </div>

        {/* Summary */}
        <div className="lg:col-span-2">
          {!summary ? (
            <div className="border border-zinc-200 bg-white p-12 text-center text-sm text-zinc-500">
              Select an account to see collection history.
            </div>
          ) : (
            <div className="space-y-4" data-testid="receiver-detail">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-2xl font-bold tracking-tight">{summary.account.owner || summary.account.account_name}</h2>
                  <p className="text-xs text-zinc-500">{summary.account.account_name}</p>
                </div>
                <Button onClick={() => setOpen(true)} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-md" data-testid="new-collection-button">
                  <ArrowDown size={16} weight="bold" className="mr-1" /> Record Collection
                </Button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Metric label="Total Received" value={`₹${summary.total_received.toLocaleString()}`} />
                <Metric label="Collected" value={`₹${summary.total_collected.toLocaleString()}`} />
                <Metric label="Pending" value={`₹${summary.pending_collection.toLocaleString()}`} accent />
                <Metric label="Commission" value={`₹${summary.commission_earned.toLocaleString()}`} />
              </div>

              <div className="border border-zinc-200 bg-white">
                <div className="p-4 border-b border-zinc-200 flex items-center gap-2"><Coins size={16} weight="bold" /><h3 className="text-sm font-bold uppercase tracking-widest">Recent Payments Received</h3></div>
                <table className="w-full data-table">
                  <thead><tr><th className="text-left px-4 py-2">Date</th><th className="text-left px-4 py-2">Order</th><th className="text-left px-4 py-2">Txn ID</th><th className="text-right px-4 py-2">Amount</th><th className="text-right px-4 py-2">Commission</th></tr></thead>
                  <tbody>
                    {summary.recent_payments.map((p) => (
                      <tr key={p.payment_id} className="border-b border-zinc-100" data-testid={`payment-${p.payment_id}`}>
                        <td className="px-4 py-2 text-xs">{formatDateTime(p.verified_at)}</td>
                        <td className="px-4 py-2 text-xs font-mono">{p.order_id.substring(0, 12)}</td>
                        <td className="px-4 py-2 text-xs font-mono">{p.transaction_id || "—"}</td>
                        <td className="px-4 py-2 text-sm font-mono text-right">₹{Number(p.amount).toLocaleString()}</td>
                        <td className="px-4 py-2 text-sm font-mono text-right">₹{Number(p.commission_earned).toLocaleString()}</td>
                      </tr>
                    ))}
                    {summary.recent_payments.length === 0 && <tr><td colSpan={5} className="p-4 text-center text-sm text-zinc-500">No payments yet.</td></tr>}
                  </tbody>
                </table>
              </div>

              <div className="border border-zinc-200 bg-white">
                <div className="p-4 border-b border-zinc-200 flex items-center gap-2"><ArrowDown size={16} weight="bold" /><h3 className="text-sm font-bold uppercase tracking-widest">Collections</h3></div>
                <table className="w-full data-table">
                  <thead><tr><th className="text-left px-4 py-2">Date</th><th className="text-left px-4 py-2">Method</th><th className="text-left px-4 py-2">Reference</th><th className="text-right px-4 py-2">Amount</th></tr></thead>
                  <tbody>
                    {summary.recent_collections.map((c) => (
                      <tr key={c.collection_id} className="border-b border-zinc-100">
                        <td className="px-4 py-2 text-xs">{formatDateTime(c.date)}</td>
                        <td className="px-4 py-2 text-xs">{c.method}</td>
                        <td className="px-4 py-2 text-xs font-mono">{c.reference || "—"}</td>
                        <td className="px-4 py-2 text-sm font-mono text-right">₹{Number(c.amount).toLocaleString()}</td>
                      </tr>
                    ))}
                    {summary.recent_collections.length === 0 && <tr><td colSpan={4} className="p-4 text-center text-sm text-zinc-500">No collections yet.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle>Record Collection from {selected?.owner || selected?.account_name}</DialogTitle></DialogHeader>
          <form onSubmit={addCollection} className="space-y-3" data-testid="collection-form">
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Amount ₹</Label>
              <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required className="border-zinc-300 rounded-md mt-1" data-testid="collection-amount-input" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Method</Label>
              <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                <SelectTrigger className="border-zinc-300 rounded-md mt-1" data-testid="collection-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="UPI">UPI</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Reference</Label>
              <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="border-zinc-300 rounded-md mt-1" data-testid="collection-reference-input" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="border-zinc-300 rounded-md mt-1" data-testid="collection-notes-input" />
            </div>
            <Button type="submit" className="w-full bg-zinc-900 hover:bg-zinc-800 text-white rounded-md" data-testid="save-collection-button">Record</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Metric({ label, value, accent }) {
  return (
    <div className={`border p-4 bg-white ${accent ? "border-zinc-900" : "border-zinc-200"}`}>
      <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-1">{label}</p>
      <p className="text-xl font-black tracking-tighter font-mono">{value}</p>
    </div>
  );
}
